import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface SportNotification {
  id: number;
  message: string;
  sport: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  targetApp: string | null;
  targetTier: string | null;
  authTarget: string;
  created: string;
  lastModified: string | null;
}

interface Subscription {
  productTier: string | null;
  isActive: boolean;
}

const getBaseUrl = (): string => {
  return process.env.REACT_APP_DFS_MARKETING_API || '';
};

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function isDismissed(id: number): boolean {
  return localStorage.getItem(`sport_notif_dismissed_${id}`) === 'true';
}

export default function SportNotificationBar() {
  const params: { league?: string } = useParams();
  const selectedSport = useSelector((state: any) => state.common.selectedSport);
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();

  const league = params.league || selectedSport?.sport || '';

  const [notifications, setNotifications] = useState<SportNotification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());
  const [userTiers, setUserTiers] = useState<string[]>([]);
  const [tiersLoaded, setTiersLoaded] = useState(false);

  // Fetch user tier for tier targeting (follows BannerManager pattern)
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setTiersLoaded(true);
      return;
    }

    let cancelled = false;

    async function loadTiers() {
      const baseUrl = getBaseUrl();
      if (!baseUrl) { setTiersLoaded(true); return; }

      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${baseUrl}/api/subscriptions/my-tier`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && !cancelled) {
          const subs: Subscription[] = await res.json();
          const tiers = subs
            .filter((s) => s.isActive && s.productTier)
            .map((s) => s.productTier!.toLowerCase());
          setUserTiers(tiers);
        }
      } catch {
        // Tier fetch failed silently — will show non-tier-targeted notifications
      }
      if (!cancelled) setTiersLoaded(true);
    }

    loadTiers();
    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  const fetchNotifications = useCallback(async () => {
    const baseUrl = getBaseUrl();
    if (!baseUrl || !league) return;

    try {
      const res = await fetch(
        `${baseUrl}/api/sport-notifications/active?sport=${encodeURIComponent(league)}&app=stokastic-tools`
      );
      if (!res.ok) return;

      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) return;

      const data: SportNotification[] = await res.json();
      setNotifications(data);
    } catch {
      // Fetch failed silently
    }
  }, [league]);

  // Initial fetch and polling every 60s
  useEffect(() => {
    if (authLoading) return;

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, authLoading]);

  const handleDismiss = (id: number) => {
    localStorage.setItem(`sport_notif_dismissed_${id}`, 'true');
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  // Filter: auth target + tier target + dismissals
  const visibleNotifications = notifications.filter((notif) => {
    if (isDismissed(notif.id) || dismissedIds.has(notif.id)) return false;
    if (notif.authTarget === 'LoggedIn' && !isAuthenticated) return false;
    if (notif.authTarget === 'LoggedOut' && isAuthenticated) return false;

    // Tier targeting: if targetTier is set, only show to users with that tier
    if (notif.targetTier) {
      if (!isAuthenticated) return false;
      if (!tiersLoaded) return false;
      if (!userTiers.some((t) => t === notif.targetTier!.toLowerCase())) return false;
    }

    return true;
  });

  if (visibleNotifications.length === 0) return null;

  return (
    <Box
      sx={{
        maxHeight: 120,
        overflowY: 'auto',
        bgcolor: 'rgba(14, 86, 242, 0.08)',
        borderBottom: '1px solid rgba(14, 86, 242, 0.15)',
      }}
    >
      {visibleNotifications.map((notif) => (
        <Stack
          key={notif.id}
          direction="row"
          alignItems="center"
          sx={{
            px: 2,
            py: 0.75,
            '&:not(:last-child)': {
              borderBottom: '1px solid rgba(14, 86, 242, 0.1)',
            },
          }}
        >
          <InfoOutlinedIcon
            sx={{ color: 'rgba(14, 86, 242, 0.6)', fontSize: 18, mr: 1, flexShrink: 0 }}
          />
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: 12,
              fontFamily: 'Inter, sans-serif',
              whiteSpace: 'nowrap',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            {formatTimestamp(notif.created)}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: 13,
              fontFamily: 'Inter, sans-serif',
              flex: 1,
              minWidth: 0,
            }}
          >
            {notif.message}
          </Typography>
          <IconButton
            onClick={() => handleDismiss(notif.id)}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              ml: 1,
              flexShrink: 0,
              '&:hover': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      ))}
    </Box>
  );
}
